import { apiClient } from './config.js';
import type {
  PaginatedResponse,
  AnnouncementSummary,
  AnnouncementDetail,
  AnnouncementUploadRequest,
  AnnouncementUploadResponse,
  AnnouncementListParams,
  ArticleWithContext,
  AnnouncementContactExtraction,
  PipelineJobSummary,
} from './types.js';

export async function getAnnouncementPipelineJobs(
  announcementId: string,
): Promise<PipelineJobSummary[]> {
  const response = await apiClient.get<PipelineJobSummary[]>(
    `/announcements/${announcementId}/pipeline-jobs`,
  );
  return response.data;
}

/**
 * Upload a new announcement PDF
 * Metadata is automatically extracted by AI
 */
export async function uploadAnnouncement(
  request: AnnouncementUploadRequest
): Promise<AnnouncementUploadResponse> {
  const formData = new FormData();
  formData.append('file', request.file);

  // Add user email if provided
  if (request.uploadedByEmail) {
    formData.append('uploaded_by_email', request.uploadedByEmail);
  }

  const response = await apiClient.post<AnnouncementUploadResponse>(
    '/announcements',
    formData,
    {
      headers: {
        // Let axios automatically set Content-Type with boundary for multipart/form-data
        'Content-Type': undefined,
      },
      // File uploads need longer timeout than standard API calls (5 minutes)
      timeout: 300000,
    }
  );

  return response.data;
}

/**
 * Multi-file batch upload (combined-mode or independent-mode).
 *
 * Submits a batch of PDFs and/or markdown sources, optionally with
 * per-source attached images, to /announcements/batch. Combined-mode
 * produces one combined article via the planner's primary+secondaries
 * path. Independent-mode keeps the legacy inline-generation per file.
 * See docs/wip/assayer-stream/BATCH_UPLOAD_PLAN.md.
 */
export interface BatchFileSpec {
  filename: string;
  source_type: 'announcement' | 'research_pdf' | 'research_markdown' | 'editorial_brief';
  body_passthrough?: boolean;
  override_target_id?: string;
  override_persona_slug?: string;
  attached_images?: string[];  // filenames cross-referencing the images[] parts
  /** FT-3: research-document provenance — feeds the document-source isBasedOn. */
  source_url?: string;
  source_publisher?: string;
  /** FT-4: per-piece byline ruling (operator 2026-07-30). */
  byline_mode?: 'house_persona' | 'human';
  human_byline?: string;
  /** Editorial commissions (§7.4 decision 2): attach this upload to a
   *  pending commission part — the body-arrives-written case. */
  commission_part_id?: string;
}

export interface BatchUploadRequest {
  batch_mode: 'combined' | 'independent';
  uploaded_by?: string;
  batch_label?: string;
  files: File[];                       // source documents
  fileSpecs: BatchFileSpec[];          // 1:1 with files[]
  images?: File[];                     // optional attached images
}

export interface BatchUploadResult {
  batch_id: string;
  batch_mode: string;
  files: Array<{
    announcement_id: string;
    filename: string;
    deduped: boolean;
    attached_image_count: number;
  }>;
}

export async function uploadAnnouncementBatch(
  request: BatchUploadRequest
): Promise<BatchUploadResult> {
  const formData = new FormData();
  const spec = {
    batch_mode: request.batch_mode,
    uploaded_by: request.uploaded_by,
    batch_label: request.batch_label,
    files: request.fileSpecs,
  };
  formData.append('batch_spec', JSON.stringify(spec));
  for (const f of request.files) {
    formData.append('files', f, f.name);
  }
  for (const img of request.images || []) {
    formData.append('images', img, img.name);
  }
  const response = await apiClient.post<BatchUploadResult>(
    '/announcements/batch',
    formData,
    {
      headers: { 'Content-Type': undefined },
      timeout: 600000, // batches can be large
    }
  );
  return response.data;
}

export interface BatchStatus {
  batch: {
    batch_id: string;
    source_name: string;
    status: string;
    batch_mode: string;
    announcement_count: number;
    uploaded_by?: string;
    batch_label?: string;
    batch_started_at: string;
    batch_completed_at?: string;
  };
  announcements: Array<{
    announcement_id: string;
    original_filename: string;
    source_type: string;
    file_type: string;
    processing_status: string;
    body_passthrough: boolean;
    entity_id?: string;
    company_code?: string;
    announcement_title?: string;
    error_message?: string | null;
  }>;
  plan_item_id?: string;
  article_id?: string;
}

export async function getBatchStatus(batchId: string): Promise<BatchStatus> {
  const response = await apiClient.get<BatchStatus>(
    `/announcements/batch/${batchId}`
  );
  return response.data;
}

/**
 * List operator-pickable personas for an egress_target.
 * Used by the body-passthrough flow on UploadPage.
 */
export interface PersonaOption {
  slug: string;
  display_name: string;
  analytical_lens?: string;
}

export interface PersonasForTarget {
  target_id: string;
  target_name: string;
  personas: PersonaOption[];
}

export async function getPersonasForTarget(
  targetId: string
): Promise<PersonasForTarget> {
  const response = await apiClient.get<PersonasForTarget>(
    `/skills/personas?target_id=${encodeURIComponent(targetId)}`
  );
  return response.data;
}

/**
 * List announcements with optional filters
 */
export async function listAnnouncements(
  params?: AnnouncementListParams
): Promise<PaginatedResponse<AnnouncementSummary>> {
  const response = await apiClient.get<PaginatedResponse<AnnouncementSummary>>(
    '/announcements',
    { params }
  );

  return response.data;
}

/**
 * Get announcement details
 */
export async function getAnnouncement(
  announcementId: string
): Promise<AnnouncementDetail> {
  const response = await apiClient.get<AnnouncementDetail>(
    `/announcements/${announcementId}`
  );

  return response.data;
}

/**
 * Get announcements with articles (gallery view)
 */
export async function getAnnouncementsWithArticles(
  params?: AnnouncementListParams
): Promise<PaginatedResponse<ArticleWithContext>> {
  const response = await apiClient.get<PaginatedResponse<ArticleWithContext>>(
    '/announcements/gallery',
    { params }
  );

  return response.data;
}

/**
 * Regenerate article with custom voice and AI provider
 */
export async function regenerateArticle(
  announcementId: string,
  voiceName: string,
  provider: 'anthropic' | 'openai',
  options?: {
    persona_slug?: string;
    target_name?: string;
    destination_name?: string;
    /** Optional operator-supplied guidance — surfaced to the LLM as
     *  "avoid these patterns from the previous draft". Used when
     *  regenerating after a Quality Gate `review_blocked` verdict. */
    avoidance_guidance?: string;
    /** WP-14: fully unpublish the current copy and retire its sticky slug
     *  before regenerating, so the replacement mints a fresh URL. */
    fresh_slug?: boolean;
    /** Required with fresh_slug — the article whose copies/slug are retired. */
    unpublish_article_id?: string;
  }
) {
  const params: Record<string, string> = {
    voice_name: voiceName,
    provider: provider,
  };
  if (options?.persona_slug) params.persona_slug = options.persona_slug;
  if (options?.target_name) params.target_name = options.target_name;
  if (options?.destination_name) params.destination_name = options.destination_name;
  if (options?.avoidance_guidance) params.avoidance_guidance = options.avoidance_guidance;
  if (options?.fresh_slug) params.fresh_slug = 'true';
  if (options?.unpublish_article_id) params.unpublish_article_id = options.unpublish_article_id;

  const response = await apiClient.post(
    `/announcements/${announcementId}/articles/regenerate`,
    null,
    { params }
  );

  return response.data;
}

/**
 * Signed URLs for an announcement's readable artefacts (60-minute expiry).
 *
 * `pdf_url` is the source document as submitted, whatever its format. A
 * markdown research report also has `rendered_pdf_url` — the typeset PDF the
 * network builds from it, which is what a reader gets from the published
 * article's source link. Null until that render exists.
 */
export interface SourceDocumentUrls {
  pdf_url: string;
  source_format: 'markdown' | 'pdf';
  source_filename: string;
  rendered_pdf_url: string | null;
  expires_in_minutes: number;
}

export async function getAnnouncementSourcePdfUrl(announcementId: string): Promise<SourceDocumentUrls> {
  const response = await apiClient.get<SourceDocumentUrls>(`/announcements/${announcementId}/source-pdf-url`);
  return response.data;
}

/**
 * Delete one or more announcements
 * Only announcements without articles can be deleted
 */
export async function deleteAnnouncements(
  announcementIds: string[]
): Promise<{
  deleted: number;
  skipped: number;
  not_found: number;
  total_requested: number;
  errors: string[] | null;
}> {
  const response = await apiClient.delete('/announcements', {
    data: announcementIds,
  });

  return response.data;
}

/**
 * Get extracted contacts for an announcement
 * Returns contact information extracted from the announcement PDF
 */
export async function getAnnouncementContacts(
  announcementId: string
): Promise<AnnouncementContactExtraction | null> {
  try {
    const response = await apiClient.get<AnnouncementContactExtraction>(
      `/announcements/${announcementId}/contacts`
    );
    return response.data;
  } catch (error: any) {
    // Return null if no contacts found (404)
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}


/** Retry processing for a FAILED upload — reuses the stored file, no
 *  re-upload. Backs the BatchProgressCard Retry action. */
export async function reprocessAnnouncement(
  announcementId: string,
): Promise<{ reprocessing: boolean; job_id: string }> {
  const r = await apiClient.post(`/announcements/${announcementId}/reprocess`);
  return r.data as any;
}

export interface ConsolidationDuplicate {
  article_id: string;
  published_slug: string | null;
  publish_status: string;
  social_post_count: number;
}

export interface ConsolidationEntry {
  product_id: string;
  product_name: string;
  anchor_article_id: string;
  anchor_slug: string | null;
  primary_announcement_id: string;
  primary_title: string;
  secondary_announcement_ids: string[];
  lead: 'new' | 'anchor';
  lead_reason: string;
  retire: ConsolidationDuplicate[];
  job_id: string | null;
}

export interface ConsolidationPlan {
  announcement_id: string;
  found: boolean;
  company_code?: string;
  session_date?: string | null;
  filing_title?: string | null;
  filing_substance?: number | null;
  processing_status?: string;
  sibling_announcement_ids: string[];
  entries: ConsolidationEntry[];
  dry_run: boolean;
  retire_duplicates?: boolean;
  retired_social_exposure?: number;
}

export type ConsolidationLead = 'auto' | 'new' | 'anchor';

/** Same-session consolidation preview: which of the issuer's existing
 *  same-day articles this filing would be folded into (on their slugs),
 *  which filing leads, and which of this filing's own articles would be
 *  retired. Nothing is enqueued. */
export async function previewConsolidation(
  announcementId: string,
  lead: ConsolidationLead = 'auto',
): Promise<ConsolidationPlan> {
  const r = await apiClient.get(`/announcements/${announcementId}/consolidate`, {
    params: { lead },
  });
  return r.data as ConsolidationPlan;
}

/** Enqueue the consolidation: one rewrite-in-place job per anchored product
 *  (same article_id / slug / date, edited=TRUE so no socials), and — with
 *  retireDuplicates — this filing's own duplicates come down after the
 *  rewrite lands. */
export async function consolidateAnnouncement(
  announcementId: string,
  opts: { lead?: ConsolidationLead; retireDuplicates?: boolean } = {},
): Promise<ConsolidationPlan> {
  const r = await apiClient.post(
    `/announcements/${announcementId}/consolidate`,
    undefined,
    { params: { lead: opts.lead ?? 'auto', retire_duplicates: opts.retireDuplicates ?? true } },
  );
  return r.data as ConsolidationPlan;
}

/** Clear a FAILED upload item: archives the failed stub (tags its error)
 *  so the batch card can drop it; the same file can then be re-uploaded
 *  (dedup excludes failed rows). */
export async function archiveFailedAnnouncement(
  announcementId: string,
): Promise<{ archived: number }> {
  const r = await apiClient.post('/tools/archive-failed-announcements', {
    announcement_ids: [announcementId],
  });
  return r.data as any;
}
